import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import report from "../VehicleReportAI/style";
import Stack from "react-bootstrap/Stack";
import styled from "styled-components";
import {
  Button,
  Modal,
  Badge,
  Space,
  Row,
  Card,
  Col,
  Select,
  Form,
  DatePicker,
  message,
  Radio,
  Spin,
} from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import FilterIcon from "../../../assets/images/Filter-Icon.svg";
import ExportIcon from "../../../assets/images/Export-Icon.svg";
import {
  TotalConsignment,
  VerticalChart,
  VRvehiclePerformance,
  VehicleMileage,
} from "../VehicleReportAI/VehicleChart";
import { ButtonFilled } from "../../../Reusablecomponents/UIButtons";
import { CALL_API } from "../../../Common/api";
import { handleDownloadChart } from "../../../Reusablecomponents/Export/Exportpdf";
import { shipmentType } from "../../../Common/constant";
import ReusablePagination from "../../../Reusablecomponents/ReusablePagination";
import { StyledTable } from "../../../Reusablecomponents/UITable/UITable";
import { fetchNexusAssets, fetchNexusFences } from "../../TrackRightAI/nexusTrakingApi";
import { withNexusReportShell } from "../NexusReportShell";

// Nexus Vehicle Report: same page and charts as the AI Vehicle Report, built
// from the Nexus trip tables (POST nexus/vehiclereport).

const { Option } = Select;
const { RangePicker } = DatePicker;

const StyledVehicleReport = styled.div`
  .vehicle-item {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
  }
  .text-content {
    line-height: 1.8;
    text-align: center;
  }
  .vr {
    width: 1px;
    height: 100%;
    background-color: #000;
    margin-left: 120px;
  }
  .header {
    display: flex;
    align-items: center;
    width: 100%;
  }
  .export {
    margin-left: auto;
    display: flex;
    align-items: center;
  }
`;

const Duration = [
  { label: "Current Month", value: "current_month" },
  { label: "Current Year", value: "current_year" },
  { label: "Last Month", value: "last_month" },
  { label: "Today", value: "today" },
];

const rangeFor = (duration) => {
  if (duration === "today") return [dayjs(), dayjs()];
  if (duration === "last_month")
    return [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")];
  if (duration === "current_year") return [dayjs().startOf("year"), dayjs().endOf("year")];
  return [dayjs().startOf("month"), dayjs().endOf("month")];
};

const ITEMS_PER_PAGE = 10;

const NexusVehicleReport = () => {
  const [form] = Form.useForm();
  const userInfo = useSelector((state) => state?.authTracking?.userInfo);
  const transporterList = useSelector((state) => state?.authTracking?.transporterList);
  const reduxPermissions = useSelector((state) => state?.authTracking?.myPermissionList || {});
  const exportAllowed = useMemo(
    () => Array.isArray(reduxPermissions?.NE_VRPT) && reduxPermissions.NE_VRPT.includes("EX"),
    [reduxPermissions],
  );

  const [duration, setDuration] = useState("current_month");
  const [selectedDate, setSelectedDate] = useState(rangeFor("current_month"));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [formValues, setFormValues] = useState({});
  const [chartData, setChartData] = useState({});
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [total, setTotal] = useState(0);
  const [csvList, setCsvList] = useState([]);
  const [csvLoader, setCsvLoader] = useState(false);
  const csvLinkEl = useRef();
  const [pageSize, setPageSize] = useState(10);
  const [tablePage, setTablePage] = useState(1);
  const [consignmentPage, setConsignmentPage] = useState(0);
  const [performancePage, setPerformancePage] = useState(0);
  const [mileagePage, setMileagePage] = useState(0);
  const [tableSort, setTableSort] = useState({ field: null, order: null });

  // filter options (TraKing vehicles / fences of the logged-in company)
  const [vehicles, setVehicles] = useState([]);
  const [consignors, setConsignors] = useState([]);
  const [consignees, setConsignees] = useState([]);
  useEffect(() => {
    fetchNexusAssets()
      .then((r) => setVehicles(r.response || []))
      .catch(() => setVehicles([]));
    if (userInfo?.MCID === 59 || !userInfo?.NexusCompId) return;
    fetchNexusFences("Consignor", userInfo.NexusCompId).then(setConsignors).catch(() => setConsignors([]));
    fetchNexusFences("Consignee", userInfo.NexusCompId).then(setConsignees).catch(() => setConsignees([]));
  }, [userInfo?.MCID, userInfo?.NexusCompId]);
  const driverNames = useMemo(
    () =>
      Array.from(
        new Set(vehicles.map((a) => a.driver_name || a.master_driver_name).filter(Boolean)),
      ),
    [vehicles],
  );

  useEffect(() => {
    setSelectedDate(rangeFor(duration));
  }, [duration]);

  const requestBody = useCallback(
    (page, size) => ({
      dateFrom: dayjs(selectedDate?.[0]).format("YYYY-MM-DD"),
      dateTo: dayjs(selectedDate?.[1]).format("YYYY-MM-DD"),
      reportType: formValues?.ReportType ?? 0,
      selectedOption: formValues?.SelectedOption || "Vehicle",
      vehicle: formValues?.vehicle || "",
      consigner: formValues?.consignor || "",
      transporter: formValues?.transporter || "",
      consignee: formValues?.fence || "",
      driver: formValues?.driver || "",
      tripType: formValues?.tripType || "",
      shipmentType: formValues?.shipment || "",
      page,
      pageSize: size,
    }),
    [selectedDate, formValues],
  );

  const fetchReport = useCallback(async () => {
    if (!selectedDate?.[0] || !selectedDate?.[1]) return;
    setLoading(true);
    try {
      const { data } = await CALL_API("POST", "nexus/vehiclereport", requestBody(tablePage, pageSize));
      setChartData(data || {});
      setTableData(data?.vehicleTable || []);
      setTotal(data?.totalVehicle || 0);
    } catch (error) {
      console.error("[NexusVehicleReport] fetch error:", error);
      message.error("Unable to load the vehicle report.");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, requestBody, tablePage, pageSize]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // filters or dates start the charts and the table from the first page
  useEffect(() => {
    setTablePage(1);
    setConsignmentPage(0);
    setPerformancePage(0);
    setMileagePage(0);
    setFilterCount(Object.values(formValues).filter((v) => v !== undefined && v !== null && v !== "").length);
  }, [formValues, selectedDate]);

  const selectedOption = formValues?.SelectedOption || "Vehicle";
  const reportIsTrips = formValues?.ReportType === 1;

  const page = (list, p) => (list || []).slice(p * ITEMS_PER_PAGE, (p + 1) * ITEMS_PER_PAGE);
  const pager = (list, p, setP) => {
    const hasPrev = p > 0;
    const hasNext = (p + 1) * ITEMS_PER_PAGE < (list?.length || 0);
    return (
      <>
        <LeftOutlined
          onClick={() => hasPrev && setP(p - 1)}
          style={{ cursor: hasPrev ? "pointer" : "not-allowed", color: hasPrev ? "black" : "#ccc" }}
        />
        <RightOutlined
          onClick={() => hasNext && setP(p + 1)}
          style={{ marginLeft: "15px", cursor: hasNext ? "pointer" : "not-allowed", color: hasNext ? "black" : "#ccc" }}
        />
      </>
    );
  };
  const exportButton = (elementId, title) => (
    <Button style={report.exportbtn}>
      <img
        src={ExportIcon}
        alt="Export Icon"
        width={20}
        style={{ cursor: exportAllowed ? "pointer" : "not-allowed" }}
        onClick={() => {
          if (!exportAllowed) return;
          handleDownloadChart(elementId, title);
        }}
      />
    </Button>
  );

  const sortRows = (rows) => {
    if (!tableSort.field) return rows;
    const numeric = ["Number of Trips Done", "Number of Consignments", "Total Distance Covered"];
    return [...rows].sort((a, b) => {
      const av = a[tableSort.field];
      const bv = b[tableSort.field];
      const r = numeric.includes(tableSort.field)
        ? (Number(av) || 0) - (Number(bv) || 0)
        : String(av ?? "").localeCompare(String(bv ?? ""));
      return tableSort.order === "ascend" ? r : -r;
    });
  };

  const col = (title, numeric) => ({
    title,
    dataIndex: title,
    key: title,
    sorter: true,
    sortOrder: tableSort.field === title ? tableSort.order : null,
    render: (v) => (v === undefined || v === null || v === "" ? "-" : v),
    ...(numeric ? {} : {}),
  });
  const columns = [
    col("Vehicle Reg"),
    col("Driver Name"),
    col("Number of Trips Done", true),
    col("Number of Consignments", true),
    col("Total Distance Covered", true),
  ];

  const prepareCSV = async () => {
    try {
      setCsvLoader(true);
      const { data } = await CALL_API("POST", "nexus/vehiclereport", requestBody(1, 5000));
      const rows = sortRows(data?.vehicleTable || []);
      if (!rows.length) {
        message.error("No data available to download.");
        return;
      }
      setCsvList(rows);
      setTimeout(() => csvLinkEl.current?.link?.click(), 300);
    } catch (error) {
      console.error("[NexusVehicleReport] export error:", error);
      message.error("Unable to export the vehicle report.");
    } finally {
      setCsvLoader(false);
    }
  };

  const consignmentChartData = page(chartData?.TotalConsignmentChart, consignmentPage);
  const performanceData = chartData?.mileage || [];

  return (
    <StyledVehicleReport>
      {/* Top Tab Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "18px 28px 0 28px",
          borderBottom: "1px solid #f0f0f0",
          gap: 8,
        }}
      >
        <button
          style={{
            background: "#e8f5e9",
            border: "none",
            fontSize: 14,
            color: "#3a8f3a",
            fontWeight: 600,
            cursor: "pointer",
            padding: "6px 18px",
            borderRadius: 20,
          }}
        >
          Vehicle Report
        </button>
      </div>

      <Row style={{ borderRadius: "0px" }}>
        <Col span={24}>
          <Card bordered={false}>
            <div style={report.firstcontan}>
              <div style={report.overview}>
                <span>Chart view</span>
              </div>

              <div style={report.controls}>
                <div style={report.subcontainer}>
                  <Select style={{ width: "240px" }} value={duration} onChange={setDuration}>
                    {Duration.map((item) => (
                      <Option key={item.value} value={item.value}>
                        {item?.label}
                      </Option>
                    ))}
                  </Select>
                </div>

                <div style={report.subcontainer}>
                  <RangePicker
                    value={selectedDate}
                    onChange={(date) => date && setSelectedDate(date)}
                  />
                </div>

                <div style={report.subcontainer}>
                  <Button style={report.filterbtn} onClick={() => setIsModalOpen(true)}>
                    <Space size="middle">
                      <Badge count={filterCount}>
                        <img src={FilterIcon} alt="Filter Icon" width={20} />{" "}
                      </Badge>
                    </Space>
                  </Button>
                </div>

                <Modal
                  open={isModalOpen}
                  onCancel={() => setIsModalOpen(false)}
                  width={900}
                  footer={
                    <ButtonFilled
                      onClick={() => {
                        form.resetFields();
                        setFormValues({});
                      }}
                    >
                      Clear All
                    </ButtonFilled>
                  }
                >
                  <Row>
                    <Col xl={24} xs={{ span: 24, order: 2 }}>
                      <div className="container_for_kpi_filter">
                        <h6 className="Filter_for_clickablerow">Filter ({filterCount})</h6>
                      </div>
                    </Col>
                  </Row>
                  <Form
                    form={form}
                    layout="vertical"
                    onValuesChange={(value, allvalues) => setFormValues(allvalues)}
                  >
                    <Row gutter={16}>
                      <Col xs={24} sm={24} md={6}>
                        <Form.Item label="Report Type" name="ReportType">
                          <Select className="w-100" showSearch allowClear>
                            <Option value={0}>Consignment</Option>
                            <Option value={1}>Trip</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={6}>
                        <Form.Item label="Vehicle" name="vehicle">
                          <Select
                            className="w-100"
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            options={vehicles.map((a) => ({ label: a.asset_name, value: a.asset_id }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={6}>
                        <Form.Item label="Consignor" name="consignor">
                          <Select
                            className="w-100"
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            options={consignors.map((f) => ({ label: f.name, value: String(f.geofence_id) }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={6}>
                        <Form.Item label="Transporter" name="transporter">
                          <Select
                            className="w-100"
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            options={transporterList?.map((item) => ({ label: item.Name, value: item.ID }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={6}>
                        <Form.Item label="Consignee" name="fence">
                          <Select
                            className="w-100"
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            options={consignees.map((f) => ({ label: f.name, value: f.name }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={6}>
                        <Form.Item label="Driver" name="driver">
                          <Select
                            className="w-100"
                            showSearch
                            allowClear
                            options={driverNames.map((n) => ({ label: n, value: n }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={6}>
                        <Form.Item label="Trip Type" name="tripType">
                          <Select className="w-100" showSearch allowClear>
                            <Option value={2}>Safari</Option>
                            <Option value={1}>Local</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={6}>
                        <Form.Item label="Shipment Type" name="shipment">
                          <Select
                            className="w-100"
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            options={shipmentType?.map((item) => ({ label: item.name, value: item.value }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={6}>
                        <Form.Item label="View Data by" name="SelectedOption">
                          <Radio.Group>
                            <Radio value="Driver">Drivers</Radio>
                            <Radio value="Vehicle">Vehicles</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </Modal>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col
          sm={24}
          md={24}
          style={{
            display: "flex",
            justifyContent: "space-around",
            textAlign: "center",
            backgroundColor: "#F4F4F4",
          }}
        >
          {[{ Name: "Total No Of Vehicles", values: total }].map((item, index) => (
            <div key={index} className="vehicle-item">
              <Stack direction="horizontal">
                <div className="text-content">
                  <h6>{selectedOption === "Driver" ? "Total No Of Drivers" : item.Name}</h6>
                  <h3>{item.values}</h3>
                </div>
              </Stack>
            </div>
          ))}
        </Col>

        <Col sm={24} md={24}>
          <Spin spinning={loading}>
            <Card
              title={`Total ${reportIsTrips ? "Trips" : "Consignments"} (${
                reportIsTrips ? chartData?.TotalTrips ?? 0 : chartData?.TotalConsignments ?? 0
              })`}
              className="mt-3"
              id="Total Consignment"
              extra={
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {pager(chartData?.TotalConsignmentChart, consignmentPage, setConsignmentPage)}
                  {exportButton("Total Consignment", `Total ${reportIsTrips ? "Trips" : "Consignments"}`)}
                </div>
              }
            >
              <TotalConsignment
                data={consignmentChartData}
                width="100%"
                height={350}
                selectedOption={selectedOption}
              />
            </Card>
          </Spin>
        </Col>

        <Col sm={24} md={24}>
          <br />
          <div id="VehiclesPerformanceChart">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h5>{`Vehicle's Performance per ${selectedOption === "Driver" ? "Driver" : "Vehicle"} `}</h5>
            </div>

            <div style={{ display: "flex", gap: "20px" }}>
              <Col sm={24} md={12}>
                <Spin spinning={loading}>
                  <div id="MostUtilized">
                    <Card className="mt-3" title={<h6>Most Utilized</h6>} extra={exportButton("MostUtilized", "Most Utilized")}>
                      <VerticalChart
                        data={chartData?.MostUtilized?.map((item) => ({
                          Vehicle_No: item?.Data,
                          Driver_Name: item?.Data,
                          value: item?.ConsignmentCount,
                        }))}
                        width="100%"
                        height={380}
                        type={"Vehicle-No"}
                        barColor="#AC6DFF"
                        selectedOption={selectedOption}
                      />
                    </Card>
                  </div>
                </Spin>
              </Col>

              <Col sm={24} md={12}>
                <Spin spinning={loading}>
                  <div id="LeastUtilized">
                    <Card className="mt-3" title={<h6>Least Utilized</h6>} extra={exportButton("LeastUtilized", "Least Utilized")}>
                      <VerticalChart
                        data={chartData?.LeastUtilized?.map((item) => ({
                          Vehicle_No: item?.Data,
                          Driver_Name: item?.Data,
                          value: item?.ConsignmentCount,
                        }))}
                        width="100%"
                        height={380}
                        barColor="#3C70AB"
                        selectedOption={selectedOption}
                      />
                    </Card>
                  </div>
                </Spin>
              </Col>
            </div>
          </div>
        </Col>

        <Col sm={24} md={24}>
          <Spin spinning={loading}>
            <div id="VehiclePerformanceData">
              <Card className="mt-3 ">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px 0px" }}>
                  <h5>Vehicle Performance Data</h5>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {pager(performanceData, performancePage, setPerformancePage)}
                    {exportButton("VehiclePerformanceData", "Vehicle Performance Data")}
                  </div>
                </div>
                <VRvehiclePerformance data={page(performanceData, performancePage)} width="100%" height={350} />
              </Card>
            </div>
          </Spin>
        </Col>

        <Col sm={24} md={24}>
          <Spin spinning={loading}>
            <div id="VehicleMileage">
              <Card
                title=" Vehicle Performance by Mileage (Km/L)"
                className="mt-3"
                extra={
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {pager(performanceData, mileagePage, setMileagePage)}
                    {exportButton("VehicleMileage", "Vehicle Performance by Mileage")}
                  </div>
                }
              >
                <VehicleMileage data={page(performanceData, mileagePage)} />
              </Card>
            </div>
          </Spin>
        </Col>

        <Col span={24}>
          <div className="header" style={{ margin: "24px 0 12px 0" }}>
            <span>{total || 0} {selectedOption === "Driver" ? "Drivers" : "Vehicles"}</span>
            <div className="export">
              <Spin spinning={csvLoader}>
                <img
                  src={ExportIcon}
                  alt="Export"
                  onClick={() => {
                    if (!exportAllowed) return;
                    prepareCSV();
                  }}
                  style={{ cursor: exportAllowed ? "pointer" : "not-allowed" }}
                />
              </Spin>
              <CSVLink data={csvList} filename={"Nexus Vehicle Report.csv"} ref={csvLinkEl} />
            </div>
          </div>
          {tableData?.length > 0 && (
            <Spin spinning={loading}>
              <StyledTable
                dataSource={sortRows(tableData).map((r, i) => ({ ...r, key: i }))}
                columns={columns}
                pagination={false}
                style={{ width: "100%" }}
                scroll={{ x: "100%" }}
                onChange={(pagination, filters, sorter) =>
                  setTableSort({ field: sorter.field, order: sorter.order })
                }
              />
            </Spin>
          )}
        </Col>
      </Row>
      <ReusablePagination
        pageSize={pageSize}
        handlePageSizeChange={(v) => {
          setPageSize(v);
          setTablePage(1);
        }}
        currentPage={tablePage}
        total={total}
        handlePageChange={(p, size) => {
          setTablePage(p);
          setPageSize(size);
        }}
      />
    </StyledVehicleReport>
  );
};

export default withNexusReportShell(NexusVehicleReport);
